import { ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import type { PDFDocumentLoadingTask, RenderTask } from 'pdfjs-dist';
import { DrawingSetRevision } from '../../../../../../../../shared/models/drawing-sheet';
import { PDF_PAGE_RANGES, PdfRenderService } from '../../../../../../../../shared/services/pdf-render.service';
import { DrawingsAnalytics } from '../drawings-analytics';
import {
  anchorScrollDelta,
  buttonsReleased,
  capturePointer,
  clampZoom,
  GestureReporter,
  isPinchWheel,
  isSafariGesture,
  pageFraction,
  pinchDistance,
  releasePointer,
  SafariPinch,
  stepZoom,
  wheelZoomFactor,
} from '../sheet-zoom-pan';

export interface DrawingSetPageViewerData {
  row: DrawingSetRevision;
  /** The set PDF: zoomed in, the page is drawn from it, sharp; without it the preview image is enlarged */
  fileUrl?: string | null;
}

/** Zoom steps, as the drawing viewer; the title block view is the largest, scrolled to the lower right */
const ZOOMS = [1, 1.5, 2, 3];
const TITLE_BLOCK_ZOOM = 3;
/** The sharp page is drawn this long after the last zoom change */
const SHARP_DEBOUNCE_MS = 250;
/**
 * A canvas larger than this many pixels is drawn smaller (review I5): just under iOS / iPadOS Safari's 16.7 MP canvas
 * limit, so iPad gets the sharp page too, and about 64 MB of backing store at most
 */
export const MAX_CANVAS_PIXELS = 16_000_000;

/**
 * The sharp page's scale (review I5): the displayed width times the device pixel ratio, stepped down so the canvas
 * stays within MAX_CANVAS_PIXELS.
 */
export function sharpScale(pageWidth: number, pageHeight: number, displayWidth: number, dpr: number): number {
  let scale = (displayWidth * dpr) / pageWidth;
  const pixels = pageWidth * pageHeight * scale * scale;
  if (pixels > MAX_CANVAS_PIXELS) {
    scale *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  }
  return scale;
}

/** A canvas no longer drawn: 0 x 0 frees its backing store at once (Safari counts it until then) */
function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}

/**
 * The set review's full-page viewer: one page as large as the screen allows, with Whole page / Title block, ± zoom
 * and drag (or scroll) to pan. The app's carousel shows a picture but cannot zoom, so this follows the drawing
 * viewer's zoom steps instead.
 */
@Component({
  selector: 'app-drawing-set-page-viewer',
  templateUrl: './drawing-set-page-viewer.component.html',
  styleUrls: ['./drawing-set-page-viewer.component.scss'],
  standalone: false,
})
export class DrawingSetPageViewerComponent implements OnDestroy {
  readonly data = inject<DrawingSetPageViewerData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<DrawingSetPageViewerComponent>>(MatDialogRef);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef<HTMLElement>);
  zoom = 1;
  @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;
  private drag: { x: number; y: number; left: number; top: number } | null = null;
  private readonly pdf = inject(PdfRenderService);
  @ViewChild('sharpHost') private sharpHost?: ElementRef<HTMLElement>;
  /** Item 9: the zoom the sharp page on screen was drawn for; null while the preview image shows alone */
  sharpZoom: number | null = null;
  /** The size the sharp page covers: the zoomed preview image's box */
  sharpBox: { width: number; height: number } | null = null;
  private doc: PDFDocumentLoadingTask | null = null;
  /** A failed PDF load is tried again once, on a later zoom-in, never in a loop */
  private docFailures = 0;
  /** The render in flight: cancelled on a zoom change, at zoom 1 and on close */
  private renderTask: RenderTask | null = null;
  /** The canvas on screen, released (0 x 0) when replaced or no longer shown */
  private sharpCanvas: HTMLCanvasElement | null = null;
  private readonly zoomChanged = new Subject<number>();
  private renderSeq = 0;
  private readonly track = inject(DrawingsAnalytics);
  /** W48: the first sharp render of this open is reported, not every zoom step */
  private sharpTracked = false;
  /** W55: two-finger pinch */
  private readonly touches = new Map<number, { x: number; y: number }>();
  private pinchStart: { distance: number; zoom: number } | null = null;
  private readonly safariPinch = new SafariPinch();
  private readonly zoomReporter = new GestureReporter<'pinch' | 'wheel'>(method => this.track.track('sheet_zoomed', { method }));
  private readonly sub = this.zoomChanged.pipe(debounceTime(SHARP_DEBOUNCE_MS)).subscribe(zoom => void this.renderSharp(zoom));

  ngOnDestroy(): void {
    this.zoomReporter.flush();
    this.sub.unsubscribe();
    this.renderSeq++;
    this.cancelRender();
    this.releaseCanvas();
    void this.doc?.destroy();
    this.doc = null;
  }

  get row(): DrawingSetRevision {
    return this.data.row;
  }

  get titleBlockZoom(): boolean {
    return this.zoom === TITLE_BLOCK_ZOOM;
  }

  setZoom(zoom: number): void {
    const el = this.scroller?.nativeElement;
    // Keep the middle of the view where it was
    const cx = el ? (el.scrollLeft + el.clientWidth / 2) / (el.scrollWidth || 1) : 0.5;
    const cy = el ? (el.scrollTop + el.clientHeight / 2) / (el.scrollHeight || 1) : 0.5;
    this.applyZoom(zoom);
    if (!el) {
      return;
    }
    if (zoom === TITLE_BLOCK_ZOOM) {
      el.scrollLeft = el.scrollWidth;
      el.scrollTop = el.scrollHeight;
    } else {
      el.scrollLeft = cx * el.scrollWidth - el.clientWidth / 2;
      el.scrollTop = cy * el.scrollHeight - el.clientHeight / 2;
    }
  }

  /** The zoom, the sharp page it needs (or the preview again at 1), and the new size rendered; no scrolling */
  private applyZoom(zoom: number): void {
    this.zoom = zoom;
    if (zoom === 1 || this.sharpZoom !== zoom) {
      // The preview shows until the page is drawn sharp at the new zoom; zoom 1 stays on the preview. A render still
      // running is stopped, and the canvas no longer shown gives its memory back.
      this.sharpZoom = null;
      this.renderSeq++;
      this.cancelRender();
      this.releaseCanvas();
    }
    if (zoom > 1 && this.data.fileUrl) {
      this.zoomChanged.next(zoom);
    }
    this.cdr.detectChanges();
  }

  zoomBy(step: number): void {
    // W55: the next step of the ladder, also from a pinch that left the zoom between steps
    this.setZoom(stepZoom(ZOOMS, this.zoom, step > 0 ? 1 : -1));
    this.track.track('sheet_zoomed', { method: 'buttons' });
  }

  /** W55 (shared with the sheet viewer): zoom keeping the page point under the cursor where it is */
  zoomAt(zoom: number, clientX: number, clientY: number): void {
    const el = this.scroller?.nativeElement;
    const image = this.elementRef.nativeElement.querySelector('[data-testid="drawing-set-viewer-image"]') as HTMLImageElement | null;
    const next = clampZoom(zoom, ZOOMS[0], ZOOMS[ZOOMS.length - 1]);
    if (!el || !image || Math.abs(next - this.zoom) < 1e-3) {
      return;
    }
    const before = image.getBoundingClientRect();
    const fx = pageFraction(clientX, before.left, before.width);
    const fy = pageFraction(clientY, before.top, before.height);
    this.applyZoom(next);
    const after = image.getBoundingClientRect();
    el.scrollLeft += anchorScrollDelta(fx, clientX, after.left, after.width);
    el.scrollTop += anchorScrollDelta(fy, clientY, after.top, after.height);
  }

  /** ctrl / ⌘ + wheel and the trackpad pinch zoom at the cursor; a plain wheel scrolls */
  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    if (this.safariPinch.active) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.zoomAt(this.zoom * wheelZoomFactor(event.deltaY), event.clientX, event.clientY);
    this.zoomReporter.step(isPinchWheel(event) ? 'pinch' : 'wheel');
  }

  showTitleBlock(): void {
    this.setZoom(TITLE_BLOCK_ZOOM);
  }

  showWholePage(): void {
    this.setZoom(1);
  }

  /** Drag to pan once zoomed in; two fingers pinch (touch) */
  onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      // One source per pinch (review N2): not while Safari's gesture events already drive it
      if (this.touches.size === 2 && !this.safariPinch.active) {
        const [a, b] = Array.from(this.touches.values());
        this.pinchStart = { distance: pinchDistance(a, b), zoom: this.zoom };
        this.drag = null;
        return;
      }
    }
    const el = this.scroller?.nativeElement;
    if (!el || this.zoom === 1 || event.button !== 0) {
      return;
    }
    event.preventDefault();
    this.drag = { x: event.clientX, y: event.clientY, left: el.scrollLeft, top: el.scrollTop };
    // Nothing on this page is clicked, so the pan may hold the pointer from the start (the release then reaches us anywhere)
    capturePointer(el, event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (event.pointerType === 'touch' && this.touches.has(event.pointerId)) {
      this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.touches.size === 2 && this.pinchStart) {
        const [a, b] = Array.from(this.touches.values());
        this.zoomAt(this.pinchStart.zoom * (pinchDistance(a, b) / this.pinchStart.distance), (a.x + b.x) / 2, (a.y + b.y) / 2);
        this.zoomReporter.step('pinch');
        return;
      }
    }
    const el = this.scroller?.nativeElement;
    if (!el || !this.drag) {
      return;
    }
    if (buttonsReleased(event)) {
      this.drag = null;
      return;
    }
    el.scrollLeft = this.drag.left - (event.clientX - this.drag.x);
    el.scrollTop = this.drag.top - (event.clientY - this.drag.y);
  }

  onPointerUp(event?: PointerEvent): void {
    if (event?.pointerType === 'touch') {
      this.touches.delete(event.pointerId);
      if (this.touches.size < 2) {
        this.pinchStart = null;
      }
    }
    if (event) {
      releasePointer(this.scroller?.nativeElement, event.pointerId);
    }
    this.drag = null;
  }

  /** The scroller lost the pointer (released elsewhere): the pan ends (review I2) */
  onCaptureLost(): void {
    this.drag = null;
  }

  /** Review M5: desktop Safari's trackpad pinch */
  onGestureStart(event: Event): void {
    this.safariPinch.start(event, this.zoom, !!this.pinchStart);
  }

  onGestureChange(event: Event): void {
    const zoom = this.safariPinch.change(event);
    if (zoom !== null && isSafariGesture(event)) {
      this.zoomAt(zoom, event.clientX, event.clientY);
      this.zoomReporter.step('pinch');
    }
  }

  onGestureEnd(event: Event): void {
    this.safariPinch.end(event);
  }

  /**
   * Draws the page from the set PDF (range requests: only this page's bytes) at the zoom's displayed size times the
   * device pixel ratio, over the enlarged preview. Any failure leaves the preview, silently.
   */
  private async renderSharp(zoom: number): Promise<void> {
    const url = this.data.fileUrl;
    const image = this.elementRef.nativeElement.querySelector('[data-testid="drawing-set-viewer-image"]') as HTMLImageElement | null;
    if (!url || zoom !== this.zoom || zoom === 1 || !image?.offsetWidth) {
      return;
    }
    if (!this.doc && this.docFailures > 1) {
      // The PDF failed twice this open: stay on the preview
      return;
    }
    const seq = ++this.renderSeq;
    const started = performance.now();
    let canvas: HTMLCanvasElement | null = null;
    try {
      this.doc ??= this.pdf.openDocument(url, PDF_PAGE_RANGES);
      let doc;
      try {
        doc = await this.doc.promise;
      } catch (error) {
        // A failed load is not kept: the next zoom-in tries once more (review I5)
        this.docFailures++;
        void this.doc?.destroy();
        this.doc = null;
        throw error;
      }
      if (seq !== this.renderSeq) {
        return;
      }
      const page = await doc.getPage(this.row.page_number);
      if (seq !== this.renderSeq) {
        // Review N2: a newer render took over while the page loaded; never cancel it from here
        return;
      }
      const base = page.getViewport({ scale: 1 });
      const width = image.offsetWidth;
      const height = image.offsetHeight;
      const scale = sharpScale(base.width, base.height, width, window.devicePixelRatio || 1);
      this.cancelRender();
      const render = this.pdf.startRender(page, scale);
      canvas = render.canvas;
      this.renderTask = render.task;
      await render.done;
      if (this.renderTask === render.task) {
        this.renderTask = null;
      }
      if (seq !== this.renderSeq || zoom !== this.zoom) {
        releaseCanvas(canvas);
        return;
      }
      canvas.classList.add('viewer-sharp-canvas');
      canvas.setAttribute('data-testid', 'drawing-set-viewer-sharp');
      this.sharpBox = { width, height };
      this.sharpZoom = zoom;
      this.cdr.detectChanges();
      this.releaseCanvas();
      this.sharpCanvas = canvas;
      this.sharpHost?.nativeElement.replaceChildren(canvas);
      this.trackSharp(true, started, zoom);
    } catch {
      // No PDF, a failed range request, a cancelled render, a page pdf.js cannot draw: the preview image stays
      if (canvas && canvas !== this.sharpCanvas) {
        releaseCanvas(canvas);
      }
      if (seq === this.renderSeq) {
        this.trackSharp(false, started, zoom);
      }
    }
  }

  private cancelRender(): void {
    this.renderTask?.cancel();
    this.renderTask = null;
  }

  /** The canvas on screen gives its backing store back and leaves the page */
  private releaseCanvas(): void {
    if (this.sharpCanvas) {
      releaseCanvas(this.sharpCanvas);
      this.sharpCanvas.remove();
      this.sharpCanvas = null;
    }
  }

  private trackSharp(ok: boolean, started: number, zoom: number): void {
    if (!this.sharpTracked) {
      this.sharpTracked = true;
      this.track.track('page_viewer_sharp_render', { ok, ms: Math.round(performance.now() - started), zoom });
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
